import { useCallback, useEffect, useMemo, useState } from "react";
import { allGuides } from "@/mocks/articles";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import {
  fetchSupabaseCollection,
  loadCachedValue,
  replaceSupabaseCollection,
  saveCachedValue,
  subscribeToCachedValue,
} from "@/integrations/supabase/contentStore";
import { toCanonicalDateFromPtBr } from "@/utils/date";

const STORAGE_KEY = "afiliabr_admin_articles";

export const ARTICLE_TYPES = ["Review", "Compra"] as const;

function normalizeArticleType(value: string | undefined) {
  const normalized = value?.trim().toLowerCase();
  if (normalized === "review") return "Review";
  if (normalized === "compra" || normalized === "guia de compra" || normalized === "comparativo") return "Compra";
  return "Review";
}

export type ArticleProductReference = {
  productSlug: string;
  badge: string;
  highlight: string;
};

export type ArticleComparisonColumns = {
  first: string;
  second: string;
  third: string;
};

export type ArticleComparisonRow = {
  feature: string;
  first: string;
  second: string;
  third: string;
};

export type ArticleSection = {
  id: string;
  type: "intro" | "products" | "guide" | "comparison";
  title?: string;
  content: string;
  products?: ArticleProductReference[];
  comparisonColumns?: ArticleComparisonColumns;
  comparisonTable?: ArticleComparisonRow[];
};

export type ArticleFaq = {
  question: string;
  answer: string;
};

export type ArticleRecord = {
  id: number;
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  type: string;
  image: string;
  heroImage: string;
  readTime: string;
  date: string;
  updatedDate: string;
  views: number;
  author: string;
  authorAvatar: string;
  authorBio: string;
  featured: boolean;
  trending: boolean;
  tags: string[];
  quickSummary: string;
  sections: ArticleSection[];
  faqs: ArticleFaq[];
  relatedArticleSlugs: string[];
  disclosure: string;
};

const defaultAuthorAvatar = "https://readdy.ai/api/search-image?query=professional%20tech%20reviewer%20portrait%20smiling%20confident%20person%20clean%20background%20editorial%20photography&width=80&height=80&seq=author1&orientation=squarish";
const defaultAuthorBio = "Nossa equipe testa e analisa produtos de tecnologia e eletrodomésticos com foco em experiência real de uso, custo-benefício e ofertas confiáveis para compra.";
const defaultAuthorName = "Equipe Mercadoai";
const defaultDisclosure = "Este artigo contém links de ofertas. Se você comprar através desses links, podemos receber uma comissão sem custo adicional para você. Isso nos ajuda a manter o site e continuar produzindo conteúdo gratuito e independente.";

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function normalizeDate(value: string | undefined, fallback: string) {
  if (!value?.trim()) return fallback;
  return value.includes("-") ? value : toCanonicalDateFromPtBr(value);
}

function normalizeStringArray(values: string[] | undefined, fallback: string[] = []) {
  const source = Array.isArray(values) ? values : fallback;
  return Array.from(new Set(source.map((value) => value.trim()).filter(Boolean)));
}

function normalizeProductReferences(values: ArticleProductReference[] | undefined) {
  return (Array.isArray(values) ? values : [])
    .map((value) => ({
      productSlug: value?.productSlug?.trim() ?? "",
      badge: value?.badge?.trim() ?? "",
      highlight: value?.highlight?.trim() ?? "",
    }))
    .filter((value) => value.productSlug);
}

function normalizeComparisonColumns(columns: ArticleComparisonColumns | undefined): ArticleComparisonColumns {
  return {
    first: columns?.first?.trim() || "Opção 1",
    second: columns?.second?.trim() || "Opção 2",
    third: columns?.third?.trim() || "Opção 3",
  };
}

function normalizeComparisonRows(rows: ArticleComparisonRow[] | undefined) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      feature: row?.feature?.trim() ?? "",
      first: row?.first?.trim() ?? "",
      second: row?.second?.trim() ?? "",
      third: row?.third?.trim() ?? "",
    }))
    .filter((row) => row.feature || row.first || row.second || row.third);
}

function normalizeSections(sections: ArticleSection[] | undefined, fallback: ArticleSection[]) {
  const source = (Array.isArray(sections) && sections.length > 0 ? sections : fallback).filter((section) => section.type !== "intro");

  return source.map((section, index) => ({
    id: section.id?.trim() || `secao-${index + 1}`,
    type: section.type,
    title: section.title?.trim() || undefined,
    content: section.content ?? "",
    products: section.type === "products" ? normalizeProductReferences(section.products) : undefined,
    comparisonColumns:
      section.type === "comparison" ? normalizeComparisonColumns(section.comparisonColumns) : undefined,
    comparisonTable: section.type === "comparison" ? normalizeComparisonRows(section.comparisonTable) : undefined,
  })) as ArticleSection[];
}

function normalizeFaqs(faqs: ArticleFaq[] | undefined, fallback: ArticleFaq[]) {
  const source = Array.isArray(faqs) && faqs.length > 0 ? faqs : fallback;
  return source
    .map((faq) => ({
      question: faq?.question?.trim() ?? "",
      answer: faq?.answer?.trim() ?? "",
    }))
    .filter((faq) => faq.question && faq.answer);
}

function createGenericArticle(guide: (typeof allGuides)[number]): ArticleRecord {
  return {
    id: guide.id,
    slug: guide.slug,
    title: guide.title,
    metaTitle: `${guide.title} | ${guide.type} Completo`,
    metaDescription: guide.excerpt,
    excerpt: guide.excerpt,
    category: guide.category,
    categorySlug: guide.categorySlug,
    type: normalizeArticleType(guide.type),
    image: guide.image,
    heroImage: guide.image,
    readTime: guide.readTime,
    date: guide.date,
    updatedDate: guide.date,
    views: guide.views,
    author: defaultAuthorName,
    authorAvatar: defaultAuthorAvatar,
    authorBio: defaultAuthorBio,
    featured: guide.featured,
    trending: guide.trending,
    tags: normalizeStringArray([guide.category, guide.type, ...guide.title.split(" ").slice(0, 3)]),
    quickSummary: `${guide.excerpt} Neste artigo, organizamos os principais pontos, critérios de compra e atalhos para encontrar as melhores ofertas cadastradas no sistema.`,
    sections: [
      {
        id: "resumo-geral",
        type: "intro",
        content: `${guide.excerpt}\n\nEste conteúdo foi estruturado para facilitar sua análise, reunindo contexto de uso, destaques da categoria e uma leitura objetiva para ajudar na decisão de compra.`,
      },
      {
        id: "ofertas-relacionadas",
        type: "products",
        title: "Produtos e Ofertas Relacionadas",
        content: "Selecione abaixo os produtos que mais fazem sentido para complementar este artigo e enviar o usuário diretamente para a melhor oferta disponível.",
        products: [],
      },
      {
        id: "o-que-avaliar",
        type: "guide",
        title: "O que avaliar antes de comprar",
        content: `Antes de fechar a compra, compare ficha técnica, experiência de uso, reputação da marca, suporte e preço atual.\n\n**Pontos essenciais**\n- Qualidade geral do produto\n- Recursos realmente úteis no dia a dia\n- Relação entre preço e entrega\n- Compatibilidade com seu perfil de uso`,
      },
      {
        id: "comparativo-rapido",
        type: "comparison",
        title: "Comparativo Rápido",
        content: "Use este quadro como referência visual para comparar níveis de entrega entre diferentes faixas ou perfis de produto.",
        comparisonColumns: {
          first: "Entrada",
          second: "Intermediário",
          third: "Premium",
        },
        comparisonTable: [
          { feature: "Preço", first: "Mais acessível", second: "Equilibrado", third: "Mais alto" },
          { feature: "Recursos", first: "Essenciais", second: "Completos", third: "Avançados" },
          { feature: "Perfil ideal", first: "Uso básico", second: "Uso frequente", third: "Máxima exigência" },
        ],
      },
    ],
    faqs: [
      {
        question: `${guide.title} vale a pena?`,
        answer: `Depende do seu orçamento e do que você espera da categoria ${guide.category.toLowerCase()}. Neste artigo, reunimos os principais critérios para facilitar essa decisão.`,
      },
      {
        question: `Como escolher a melhor opção em ${guide.category}?`,
        answer: "Priorize custo-benefício, confiabilidade, avaliações reais e a aderência do produto ao seu tipo de uso antes de comprar.",
      },
      {
        question: "Onde encontrar a melhor oferta?",
        answer: "Use os botões de oferta dentro do artigo para acessar os links de afiliado dos produtos cadastrados no sistema com mais rapidez.",
      },
    ],
    relatedArticleSlugs: [],
    disclosure: defaultDisclosure,
  };
}

function createTvArticle(guide: (typeof allGuides)[number]): ArticleRecord {
  const base = createGenericArticle(guide);
  return {
    ...base,
    metaTitle: "Melhor TV 50 Polegadas 2024 | Comparativo Completo + Onde Comprar",
    metaDescription: "Testamos 12 TVs 50 polegadas e elegemos as melhores de 2024. Veja comparativo de qualidade de imagem, smart TV, custo-benefício e onde comprar mais barato.",
    heroImage: "https://readdy.ai/api/search-image?query=multiple%2050%20inch%204K%20smart%20TVs%20comparison%20review%20editorial%20photography%20modern%20living%20room%20setup%20Samsung%20LG%20Sony%20side%20by%20side&width=1200&height=600&seq=hero1&orientation=landscape",
    quickSummary: `${guide.excerpt} Neste guia você encontra o comparativo completo, tabela de tecnologias e onde comprar pelo menor preço.`,
    sections: [
      {
        id: "resumo",
        type: "intro",
        content: `Se você está buscando a **melhor TV 50 polegadas de 2024**, chegou ao lugar certo. Testamos 12 modelos durante 30 dias cada, avaliando qualidade de imagem, sistema smart TV, desempenho em jogos, consumo de energia e custo-benefício.\n\nA TV 50 polegadas é o tamanho ideal para salas de até 20m² — grande o suficiente para uma experiência cinematográfica, mas sem ocupar toda a parede. Em 2024, o mercado evoluiu muito: praticamente todos os modelos já vêm com 4K, e a diferença está nas tecnologias de painel e nos sistemas operacionais.\n\nNeste guia completo, você vai encontrar as melhores opções por categoria de uso e orçamento, além de um comparativo técnico detalhado para você tomar a melhor decisão de compra.`,
      },
      {
        id: "top-picks",
        type: "products",
        title: "Top 5 Melhores TVs 50 Polegadas de 2024",
        content: "Após testes rigorosos, estas são as TVs 50 polegadas que mais se destacaram em 2024:",
        products: [
          {
            productSlug: "samsung-smart-tv-50-crystal-uhd-4k-2024",
            badge: "Melhor Custo-Benefício",
            highlight: "Melhor opção para quem quer qualidade 4K sem gastar muito. Sistema Tizen fluido e compatível com Alexa.",
          },
          {
            productSlug: "lg-oled-50-evo-c3-4k-120hz",
            badge: "Melhor Imagem",
            highlight: "Pretos perfeitos, contraste infinito e 120Hz para jogos. A melhor qualidade de imagem disponível em 50 polegadas.",
          },
          {
            productSlug: "sony-bravia-x80l-50-4k-google-tv",
            badge: "Melhor para Games",
            highlight: "Google TV nativo, HDMI 2.1 e ótimo desempenho para quem usa console e streaming todos os dias.",
          },
          {
            productSlug: "tcl-50-p635-4k-google-tv",
            badge: "Melhor Entrada",
            highlight: "Uma porta de entrada acessível para o 4K com Google TV nativo e boa experiência geral.",
          },
        ],
      },
      {
        id: "como-escolher",
        type: "guide",
        title: "Como Escolher a Melhor TV 50 Polegadas",
        content: `Antes de comprar uma TV 50 polegadas, você precisa considerar alguns fatores essenciais que vão determinar qual modelo é o ideal para o seu uso:\n\n**1. Distância de visualização**\nPara uma TV 50 polegadas 4K, a distância ideal de visualização é entre 1,5m e 2,5m.\n\n**2. Tipo de uso principal**\n- Filmes e séries\n- Jogos\n- Esportes ao vivo\n- Uso geral\n\n**3. Ambiente da sala**\n- Sala escura: OLED costuma entregar a melhor experiência\n- Sala clara: QLED e Neo QLED costumam se destacar\n\n**4. Sistema Smart TV**\nCompare fluidez, catálogo de apps e integração com o ecossistema que você já usa.`,
      },
      {
        id: "tecnologias",
        type: "comparison",
        title: "Crystal UHD vs QLED vs OLED: Qual é Melhor?",
        content: "Esta é a dúvida mais comum de quem está comprando uma TV 50 polegadas. Veja o comparativo completo:",
        comparisonColumns: {
          first: "Crystal UHD",
          second: "QLED",
          third: "OLED",
        },
        comparisonTable: [
          { feature: "Qualidade de Imagem", first: "Boa", second: "Muito Boa", third: "Excelente" },
          { feature: "Pretos e Contraste", first: "Regular", second: "Boa", third: "Perfeito" },
          { feature: "Brilho", first: "Médio", second: "Alto", third: "Médio-Alto" },
          { feature: "Sala Iluminada", first: "Regular", second: "Excelente", third: "Boa" },
          { feature: "Para Jogos", first: "Regular", second: "Muito Boa", third: "Excelente" },
          { feature: "Preço Médio", first: "R$ 1.800-3.000", second: "R$ 3.500-6.000", third: "R$ 5.000-9.000" },
        ],
      },
    ],
    faqs: [
      {
        question: "Qual a melhor TV 50 polegadas custo-benefício em 2024?",
        answer: "A Samsung Crystal UHD 50\" 4K continua entre as melhores opções para quem quer equilíbrio entre preço, imagem e sistema smart TV.",
      },
      {
        question: "TV 50 polegadas serve para qual tamanho de sala?",
        answer: "Geralmente funciona muito bem em salas com distância de visualização entre 1,5m e 2,5m.",
      },
      {
        question: "Vale pagar mais por OLED?",
        answer: "Para quem prioriza filmes em ambiente escuro e a melhor qualidade de imagem possível, sim. Para uso geral, QLED e Crystal UHD podem atender melhor no custo-benefício.",
      },
    ],
  };
}

function createS24Article(guide: (typeof allGuides)[number]): ArticleRecord {
  const base = createGenericArticle(guide);
  return {
    ...base,
    metaTitle: "Samsung Galaxy S24 Review: Vale a Pena? | Câmera, Galaxy AI e Bateria",
    metaDescription: guide.excerpt,
    quickSummary: "Testamos o Galaxy S24 em uso real para avaliar Galaxy AI, câmeras, tela, bateria e desempenho. Também organizamos três produtos relacionados em destaque para facilitar o clique em oferta.",
    sections: [
      {
        id: "primeiras-impressoes",
        type: "intro",
        content: `O **Samsung Galaxy S24** é um dos smartphones mais completos da categoria premium compacta. Durante nossos testes, avaliamos ergonomia, câmeras, Galaxy AI, desempenho em apps, autonomia e qualidade da tela.\n\nO modelo entrega uma experiência muito refinada para quem quer um aparelho topo de linha sem partir para tamanhos exagerados. A proposta aqui é unir desempenho, boas câmeras e recursos inteligentes em um formato confortável para o uso diário.`,
      },
      {
        id: "ofertas-em-destaque",
        type: "products",
        title: "Ofertas em Destaque para Comparar Agora",
        content: "Nesta área, você pode destacar até três produtos relacionados na mesma linha horizontal para facilitar o clique em oferta dentro do artigo.",
        products: [
          {
            productSlug: "iphone-15-128gb-preto",
            badge: "Alternativa Popular",
            highlight: "Ótima referência de comparação para quem está entre ecossistemas e quer avaliar câmeras, desempenho e longevidade.",
          },
          {
            productSlug: "samsung-galaxy-watch-6-44mm-preto",
            badge: "Ecossistema Galaxy",
            highlight: "Complemento ideal para quem busca integração com notificações, saúde e recursos inteligentes da Samsung.",
          },
          {
            productSlug: "fone-jbl-tune-770nc-bluetooth-anc",
            badge: "Acessório Recomendado",
            highlight: "Boa opção para completar a experiência móvel com cancelamento de ruído e uso diário confortável.",
          },
        ],
      },
      {
        id: "pontos-fortes",
        type: "guide",
        title: "O que mais gostamos no Galaxy S24",
        content: `**Tela e acabamento**\nA tela é brilhante, fluida e muito agradável para consumo de conteúdo, redes sociais e navegação.\n\n**Galaxy AI no dia a dia**\nOs recursos de inteligência artificial agregam valor principalmente em tradução, organização e produtividade.\n\n**Desempenho e câmeras**\nO aparelho entrega performance consistente para uso intenso e mantém boa qualidade fotográfica em diferentes cenários.`,
      },
      {
        id: "comparativo-rapido",
        type: "comparison",
        title: "Galaxy S24 em três perspectivas",
        content: "Este quadro ajuda a posicionar o Galaxy S24 dentro de uma análise rápida de perfil de compra.",
        comparisonColumns: {
          first: "Design",
          second: "Desempenho",
          third: "Custo-Benefício",
        },
        comparisonTable: [
          { feature: "Uso diário", first: "Compacto e confortável", second: "Ágil e estável", third: "Bom para quem busca premium compacto" },
          { feature: "Câmeras", first: "Conjunto equilibrado", second: "Processamento consistente", third: "Entrega sólida para a faixa" },
          { feature: "Bateria", first: "Boa autonomia", second: "Gerencia bem apps comuns", third: "Suficiente para a maioria dos usuários" },
        ],
      },
    ],
    faqs: [
      {
        question: "O Galaxy S24 vale a pena em 2024?",
        answer: "Sim, especialmente para quem quer um topo de linha compacto, com boa tela, desempenho consistente e integração forte com o ecossistema Galaxy.",
      },
      {
        question: "A bateria do Galaxy S24 dura bem?",
        answer: "Para a maioria dos usuários, sim. Em uso equilibrado, ele entrega autonomia suficiente para o dia inteiro com boa gestão de consumo.",
      },
      {
        question: "O Galaxy AI faz diferença no uso real?",
        answer: "Faz, principalmente em tarefas práticas como produtividade, tradução e alguns atalhos inteligentes, embora o impacto varie conforme o perfil de uso.",
      },
    ],
  };
}

const defaultCatalogBase: ArticleRecord[] = allGuides.map((guide) => {
  if (guide.slug === "melhor-tv-50-polegadas-2024") return createTvArticle(guide);
  if (guide.slug === "samsung-galaxy-s24-review") return createS24Article(guide);
  return createGenericArticle(guide);
});

export const defaultArticleCatalog: ArticleRecord[] = defaultCatalogBase.map((article) => {

  const related = defaultCatalogBase
    .filter((candidate) => candidate.slug !== article.slug)
    .sort((a, b) => {
      const sameCategoryScore = Number(b.category === article.category) - Number(a.category === article.category);
      if (sameCategoryScore !== 0) return sameCategoryScore;
      return b.views - a.views;
    })
    .slice(0, 3)
    .map((candidate) => candidate.slug);

  return {
    ...article,
    relatedArticleSlugs: article.relatedArticleSlugs.length > 0 ? article.relatedArticleSlugs : related,
  };
});

function createFallbackArticle(article: Partial<ArticleRecord>): ArticleRecord {
  const baseGuide = {
    id: Number(article.id ?? 0),
    slug: article.slug?.trim() || `artigo-${Date.now()}`,
    title: article.title?.trim() || "Novo Artigo",
    excerpt: article.excerpt?.trim() || "",
    category: article.category?.trim() || "Informática",
    categorySlug: article.categorySlug?.trim() || slugify(article.category?.trim() || "Informática"),
    type: normalizeArticleType(article.type),
    image: article.image?.trim() || "",
    readTime: article.readTime?.trim() || "10 min",
    date: normalizeDate(article.date, new Date().toISOString().slice(0, 10)),
    views: Number(article.views ?? 0),
    featured: Boolean(article.featured),
    trending: Boolean(article.trending),
  };

  return createGenericArticle(baseGuide);
}

function normalizeArticle(article: Partial<ArticleRecord>): ArticleRecord {
  const matchedDefault = defaultArticleCatalog.find((item) => item.slug === article.slug);
  const fallback = matchedDefault ?? createFallbackArticle(article);
  const date = normalizeDate(article.date, fallback.date);

  return {
    ...fallback,
    ...article,
    id: Number(article.id ?? fallback.id),
    slug: article.slug?.trim() || fallback.slug,
    title: article.title?.trim() || fallback.title,
    metaTitle: article.metaTitle?.trim() || fallback.metaTitle,
    metaDescription: article.metaDescription?.trim() || fallback.metaDescription,
    excerpt: article.excerpt?.trim() || fallback.excerpt,
    category: article.category?.trim() || fallback.category,
    categorySlug: slugify(article.category?.trim() || fallback.category),
    type: normalizeArticleType(article.type ?? fallback.type),
    image: article.image?.trim() || fallback.image,
    heroImage: article.heroImage?.trim() || article.image?.trim() || fallback.heroImage || fallback.image,
    readTime: article.readTime?.trim() || fallback.readTime,
    date,
    updatedDate: normalizeDate(article.updatedDate, date),
    views: Number(article.views ?? fallback.views),
    author: article.author?.trim() || fallback.author,
    authorAvatar: article.authorAvatar?.trim() || fallback.authorAvatar,
    authorBio: article.authorBio?.trim() || fallback.authorBio,
    featured: Boolean(article.featured),
    trending: Boolean(article.trending),
    tags: normalizeStringArray(article.tags, fallback.tags),
    quickSummary: article.quickSummary?.trim() || fallback.quickSummary,
    sections: normalizeSections(article.sections, fallback.sections),
    faqs: normalizeFaqs(article.faqs, fallback.faqs),
    relatedArticleSlugs: normalizeStringArray(article.relatedArticleSlugs, fallback.relatedArticleSlugs),
    disclosure: article.disclosure?.trim() || fallback.disclosure,
  };
}

type ArticleRow = {
  id: number | string;
  slug: string;
  title: string;
  meta_title: string;
  meta_description: string;
  excerpt: string;
  category: string;
  category_slug: string;
  type: string;
  image: string;
  hero_image: string;
  read_time: string;
  date: string;
  updated_date: string;
  views: number | string;
  author: string;
  author_avatar: string;
  author_bio: string;
  featured: boolean;
  trending: boolean;
  tags: string[] | null;
  quick_summary: string;
  sections: ArticleSection[] | null;
  faqs: ArticleFaq[] | null;
  related_article_slugs: string[] | null;
  disclosure: string;
};

function mapRow(row: ArticleRow): ArticleRecord {
  return normalizeArticle({
    id: Number(row.id),
    slug: row.slug,
    title: row.title,
    metaTitle: row.meta_title,
    metaDescription: row.meta_description,
    excerpt: row.excerpt,
    category: row.category,
    categorySlug: row.category_slug,
    type: row.type,
    image: row.image,
    heroImage: row.hero_image,
    readTime: row.read_time,
    date: row.date,
    updatedDate: row.updated_date,
    views: Number(row.views ?? 0),
    author: row.author,
    authorAvatar: row.author_avatar,
    authorBio: row.author_bio,
    featured: row.featured,
    trending: row.trending,
    tags: Array.isArray(row.tags) ? row.tags : [],
    quickSummary: row.quick_summary,
    sections: Array.isArray(row.sections) ? row.sections : [],
    faqs: Array.isArray(row.faqs) ? row.faqs : [],
    relatedArticleSlugs: Array.isArray(row.related_article_slugs) ? row.related_article_slugs : [],
    disclosure: row.disclosure,
  });
}

function mapToRow(article: ArticleRecord): ArticleRow {
  return {
    id: article.id,
    slug: article.slug,
    title: article.title,
    meta_title: article.metaTitle,
    meta_description: article.metaDescription,
    excerpt: article.excerpt,
    category: article.category,
    category_slug: article.categorySlug,
    type: article.type,
    image: article.image,
    hero_image: article.heroImage,
    read_time: article.readTime,
    date: article.date,
    updated_date: article.updatedDate,
    views: article.views,
    author: article.author,
    author_avatar: article.authorAvatar,
    author_bio: article.authorBio,
    featured: article.featured,
    trending: article.trending,
    tags: article.tags,
    quick_summary: article.quickSummary,
    sections: article.sections,
    faqs: article.faqs,
    related_article_slugs: article.relatedArticleSlugs,
    disclosure: article.disclosure,
  };
}

function loadFromStorage(): ArticleRecord[] {
  const parsed = loadCachedValue<Partial<ArticleRecord>[]>(STORAGE_KEY, defaultArticleCatalog);
  return parsed.map((article) => normalizeArticle(article));
}

function saveToStorage(data: ArticleRecord[]) {
  saveCachedValue(STORAGE_KEY, data.map((article) => normalizeArticle(article)));
}

async function fetchFromSupabase() {
  const rows = await fetchSupabaseCollection<ArticleRow>("articles", "id");
  return rows.map((row) => mapRow(row));
}

async function syncToSupabase(articles: ArticleRecord[]) {
  const rows = await replaceSupabaseCollection({
    table: "articles",
    rows: articles.map((article) => mapToRow(article)),
    orderBy: "id",
  });

  return (rows as ArticleRow[]).map((row) => mapRow(row));
}

export function createEmptyArticle(): Omit<ArticleRecord, "id"> {
  const today = new Date().toISOString().slice(0, 10);

  return {
    slug: "",
    title: "",
    metaTitle: "",
    metaDescription: "",
    excerpt: "",
    category: "Smartphones",
    categorySlug: "smartphones",
    type: "Review",
    image: "",
    heroImage: "",
    readTime: "10 min",
    date: today,
    updatedDate: today,
    views: 0,
    author: defaultAuthorName,
    authorAvatar: defaultAuthorAvatar,
    authorBio: defaultAuthorBio,
    featured: false,
    trending: false,
    tags: [],
    quickSummary: "",
    sections: [
      {
        id: "produtos-em-destaque",
        type: "products",
        title: "Produtos em Destaque",
        content: "",
        products: [],
      },

      {
        id: "guia-principal",
        type: "guide",
        title: "Guia Principal",
        content: "",
      },
      {
        id: "comparativo",
        type: "comparison",
        title: "Comparativo",
        content: "",
        comparisonColumns: {
          first: "Opção 1",
          second: "Opção 2",
          third: "Opção 3",
        },
        comparisonTable: [],
      },
    ],
    faqs: [
      {
        question: "",
        answer: "",
      },
    ],
    relatedArticleSlugs: [],
    disclosure: defaultDisclosure,
  };
}

export function useArticleCatalog() {
  const { session } = useSessionContext();
  const [articles, setArticles] = useState<ArticleRecord[]>(() => loadFromStorage());

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEY, defaultArticleCatalog, (value) => {
      setArticles((value as Partial<ArticleRecord>[]).map((article) => normalizeArticle(article)));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setArticles(remote);
          saveToStorage(remote);
          return;
        }

        const cached = loadFromStorage();
        if (!session || cached.length === 0) return;

        const seeded = await syncToSupabase(cached);
        if (!active) return;

        setArticles(seeded);
        saveToStorage(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const persist = useCallback((next: ArticleRecord[]) => {
    const normalized = next.map((article) => normalizeArticle(article));
    setArticles(normalized);
    saveToStorage(normalized);

    void syncToSupabase(normalized).then((remote) => {
      setArticles(remote);
      saveToStorage(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addArticle = useCallback(
    (article: Omit<ArticleRecord, "id">) => {
      const newId = Math.max(0, ...articles.map((item) => item.id)) + 1;
      persist([...articles, normalizeArticle({ ...article, id: newId })]);
    },
    [articles, persist]
  );

  const updateArticle = useCallback(
    (id: number, data: Omit<ArticleRecord, "id">) => {
      persist(articles.map((article) => (article.id === id ? normalizeArticle({ ...data, id }) : article)));
    },
    [articles, persist]
  );

  const deleteArticle = useCallback(
    (id: number) => {
      persist(articles.filter((article) => article.id !== id));
    },
    [articles, persist]
  );

  const resetToDefault = useCallback(() => {
    persist(defaultArticleCatalog);
  }, [persist]);

  const articleCategories = useMemo(
    () => Array.from(new Set(articles.map((article) => article.category))).sort((a, b) => a.localeCompare(b)),
    [articles]
  );

  return {
    articles,
    addArticle,
    updateArticle,
    deleteArticle,
    resetToDefault,
    articleCategories,
    slugify,
  };
}